import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Terminal,
  BookOpen,
  HelpCircle,
  Copy,
  Check,
  ChevronDown,
  Search,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  X,
  ExternalLink,
} from "lucide-react";
import boltzmannLogo from "../../assets/images/boltzmann_logo.png";
import "./AgentScientist.css";

const GITHUB_REPO_URL = "https://github.com/boltzmannlabs/Agent-Scientist";

const slugify = (text) =>
  `sec-${text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;

const CodeBlock = ({ label, code, copiedId, onCopy, id }) => {
  const isCopied = copiedId === id;
  return (
    <div className="as-terminal-box">
      <div className="as-terminal-header">
        <div className="as-terminal-dots">
          <div className="as-tdot as-tdot-red" />
          <div className="as-tdot as-tdot-yellow" />
          <div className="as-tdot as-tdot-green" />
          <span className="as-terminal-label">{label}</span>
        </div>
        <button
          onClick={() => onCopy(code, id)}
          className={`as-copy-btn ${isCopied ? "copied" : ""}`}
          title="Copy to clipboard"
        >
          {isCopied ? (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="as-terminal-code">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const CalloutBox = ({ type = "purple", title, children }) => {
  return (
    <div className={`as-callout ${type}`}>
      {title && (
        <div className="as-callout-title">
          {type === "green" ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-[#4F1985]" />
          )}
          <span>{title}</span>
        </div>
      )}
      <div className="as-callout-body">{children}</div>
    </div>
  );
};

const SECTIONS_CONFIG = [
  {
    id: "part-a",
    number: "01",
    shortTitle: "Overview",
    title: "Part A: Overview",
    icon: Globe,
    desc: "Positioning, problem & research friction, 6-step workflow, capabilities, research examples, and calls to action.",
    subsections: [
      "Overview",
      "The Problem & Research Friction",
      "How It Works (6-Step Workflow)",
      "Capabilities",
      "Research Examples",
      "Get Started & Repository",
    ],
  },
  {
    id: "part-b",
    number: "02",
    shortTitle: "Product Guide",
    title: "Part B: Product Guide",
    icon: Terminal,
    desc: "Comprehensive 12-chapter technical manual covering installation, configuration, scientific connectors, skills, profiles, execution, privacy, and troubleshooting.",
    subsections: [
      "01 Linux & macOS Installation",
      "02 Windows & First Use",
      "03 Models & Authentication",
      "04 Scientific Connections",
      "05 Skills & Tool Additions",
      "06 Create a Research Profile",
      "07 Update & Activate",
      "08 Conversations & Resume",
      "09 Scientific Execution",
      "10 Data, Privacy & Architecture",
      "11 Interfaces, Updates & Recovery",
      "12 Quick Reference & Troubleshooting",
    ],
  },
  {
    id: "part-c",
    number: "03",
    shortTitle: "Editorial Notes & FAQ",
    title: "Part C: Editorial Notes & FAQ",
    icon: HelpCircle,
    desc: "Frequently asked questions, publication boundaries checklist, and source basis.",
    subsections: [
      "Frequently Asked Questions",
      "Publication Checklist",
      // "Source Basis",
    ],
  },
];

const AgentScientist = () => {
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [activeSub, setActiveSub] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ 0: true, 1: false, 2: false });

  const mainContentRef = useRef(null);
  const isSwitchingRef = useRef(false);
  const targetSubRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const scrollSettleTimerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Agent Scientist Documentation | Boltzmann Labs";

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      "A scientific AI assistant for life sciences: research literature, work with data, connect scientific services, and organize your work in named research profiles.";
  }, []);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && mobileNavOpen) {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileNavOpen]);

  useEffect(() => {
    const handleScrollSpy = () => {
      if (isSwitchingRef.current) {
        if (targetSubRef.current) {
          setActiveSub(targetSubRef.current);
        }
        return;
      }

      const subs = SECTIONS_CONFIG[activeSectionIdx]?.subsections || [];
      if (!subs.length) return;

      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 70;

      if (isAtBottom) {
        setActiveSub(subs[subs.length - 1]);
        return;
      }

      if (window.scrollY < 120) {
        setActiveSub(subs[0]);
        return;
      }

      const triggerY = window.scrollY + 120;
      let foundSub = null;

      for (let j = 0; j < subs.length; j++) {
        const sub = subs[j];
        const subElem = document.getElementById(slugify(sub));
        if (subElem) {
          const top = subElem.getBoundingClientRect().top + window.scrollY;
          if (top <= triggerY) {
            foundSub = sub;
          }
        }
      }

      if (foundSub) {
        setActiveSub(foundSub);
      }
    };

    window.addEventListener("scroll", handleScrollSpy, { passive: true });
    handleScrollSpy();

    return () => {
      window.removeEventListener("scroll", handleScrollSpy);
    };
  }, [activeSectionIdx]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSelectSection = (idx) => {
    isSwitchingRef.current = true;
    const nextSubs = SECTIONS_CONFIG[idx]?.subsections || [];
    const firstSub = nextSubs.length > 0 ? nextSubs[0] : null;
    targetSubRef.current = firstSub;
    setActiveSectionIdx(idx);
    if (firstSub) setActiveSub(firstSub);

    const unlockScroll = () => {
      clearTimeout(scrollSettleTimerRef.current);
      scrollSettleTimerRef.current = setTimeout(() => {
        isSwitchingRef.current = false;
        targetSubRef.current = null;
        window.removeEventListener("scroll", handleScrollDuringSwitch);
      }, 200);
    };

    const handleScrollDuringSwitch = () => {
      unlockScroll();
    };

    window.addEventListener("scroll", handleScrollDuringSwitch, { passive: true });

    if (mainContentRef.current) {
      const topOffset = mainContentRef.current.getBoundingClientRect().top + window.scrollY - 84;
      window.scrollTo({ top: Math.max(0, topOffset), behavior: "smooth" });
    }
    unlockScroll();

    clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      isSwitchingRef.current = false;
      targetSubRef.current = null;
      window.removeEventListener("scroll", handleScrollDuringSwitch);
    }, 1200);
  };

  const scrollToSection = (secId, secIdx) => {
    const idx = secIdx !== undefined ? secIdx : 0;
    setOpenSections((prev) => ({ ...prev, [idx]: true }));
    handleSelectSection(idx);
  };

  const toggleSection = (idx, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenSections((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));

    if (typeof window !== "undefined" && window.innerWidth > 991 && idx !== activeSectionIdx) {
      handleSelectSection(idx);
    }
  };

  const scrollToSub = (subTitle, secIdx) => {
    isSwitchingRef.current = true;
    targetSubRef.current = subTitle;
    setActiveSub(subTitle);

    const isChangingSection = secIdx !== undefined && secIdx !== activeSectionIdx;

    if (isChangingSection) {
      setActiveSectionIdx(secIdx);
      setOpenSections((prev) => ({ ...prev, [secIdx]: true }));
    }

    const unlockScroll = () => {
      clearTimeout(scrollSettleTimerRef.current);
      scrollSettleTimerRef.current = setTimeout(() => {
        isSwitchingRef.current = false;
        targetSubRef.current = null;
        window.removeEventListener("scroll", handleScrollDuringSwitch);
      }, 200);
    };

    const handleScrollDuringSwitch = () => {
      unlockScroll();
    };

    window.addEventListener("scroll", handleScrollDuringSwitch, { passive: true });

    setTimeout(() => {
      const elem = document.getElementById(slugify(subTitle));
      if (elem) {
        const topOffset = elem.getBoundingClientRect().top + window.scrollY - 84;
        window.scrollTo({ top: Math.max(0, topOffset), behavior: "smooth" });
      }
      unlockScroll();
    }, isChangingSection ? 50 : 0);

    clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      isSwitchingRef.current = false;
      targetSubRef.current = null;
      window.removeEventListener("scroll", handleScrollDuringSwitch);
    }, 1200);
  };

  const searchResults = searchQuery.trim()
    ? SECTIONS_CONFIG.flatMap((sec, secIdx) =>
      sec.subsections
        .filter(
          (sub) =>
            sub.toLowerCase().includes(searchQuery.toLowerCase()) ||
            sec.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((sub) => ({
          secIdx,
          secTitle: sec.title,
          subTitle: sub,
        }))
    )
    : [];

  const handleSearchResultClick = (secIdx, subTitle) => {
    setSearchQuery("");
    scrollToSub(subTitle, secIdx);
  };

  return (
    <div className="as-doc-root">
      <div className="as-ambient-glow" aria-hidden="true" />

      <header className="as-hero-section">
        <div className="as-hero-card">
          <div className="as-hero-grid">
            <div className="as-hero-col-main">
              <nav className="as-breadcrumb" aria-label="Breadcrumb">
                <Link to="/#home" className="as-breadcrumb-link">
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Home</span>
                </Link>
                <span className="as-breadcrumb-sep">/</span>
                <span className="as-breadcrumb-current">Agent Scientist</span>
              </nav>

              <div className="as-hero-badge">
                <span className="as-badge-dot" />
                <span>AGENT SCIENTIST / BOLTZMANN LABS</span>
              </div>

              <h1 className="as-hero-title">
                Scientific work. <span className="as-hero-title-accent">Connected.</span>
              </h1>

              <p className="as-hero-doc-guide">Website Content &amp; Product Guide</p>

              <p className="as-hero-desc">
                A scientific AI assistant for life sciences: research literature, work with data,
                connect scientific services, and organize your work in named research profiles.
                Move from a research question to a tool-backed workflow.
              </p>

              <div className="as-hero-actions">
                <a
                  href={GITHUB_REPO_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="as-action-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View on GitHub</span>
                </a>
                <a
                  href="https://discord.gg/nGtFAR3hwn"
                  target="_blank"
                  rel="noreferrer"
                  className="as-action-btn secondary"
                >
                  <span>Join Discord</span>
                </a>
              </div>

              <div className="as-hero-chips">
                <span className="as-chip highlight">v1.0.0.0 Baseline</span>
                <span className="as-chip">Self-Hosted Architecture</span>
                <span className="as-chip">Boltzmann Connector</span>
                <span className="as-chip">Published 23 October 2026</span>
              </div>
            </div>

            <div className="as-hero-col-side">
              <div className="as-hero-powered-card">
                <div className="as-hero-powered-specular" aria-hidden="true" />
                <div className="as-hero-powered-glow" aria-hidden="true" />

                <div className="as-hero-powered-body">
                  <div className="as-hero-powered-logo-box">
                    <img
                      src={boltzmannLogo}
                      alt="Boltzmann Labs"
                      className="as-hero-powered-logo"
                    />
                  </div>
                  <div className="as-hero-powered-content">
                    <h2 className="as-hero-powered-title">
                      Developed by <span className="as-hero-powered-highlight">Boltzmann Labs</span>
                    </h2>
                  </div>
                </div>

                <p className="as-hero-powered-desc">
                  Transform drug discovery with Boltzmann Labs' AI platform. Accelerate small molecule design, protein engineering, clinical trials, and multi-omics analysis.
                </p>

                <div className="as-hero-powered-footer">
                  <a
                    href="https://boltzmann.co/oursuite"
                    target="_blank"
                    rel="noreferrer"
                    className="as-hero-powered-btn"
                  >
                    <span>Explore Boltzmann Labs</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="as-mobile-sticky-bar">
        <div className="as-mobile-search-pill">
          <Search className="as-mobile-search-pill-icon" />
          <input
            type="text"
            className="as-mobile-search-input"
            placeholder="Search topics, commands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search documentation topics and commands"
          />
          {searchQuery ? (
            <button
              type="button"
              className="as-mobile-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span className="as-mobile-search-pill-badge">Search</span>
          )}
        </div>

        {searchQuery.trim() && (
          <div className="as-mobile-search-dropdown">
            <div className="as-mobile-search-dropdown-header">
              <span>{searchResults.length} {searchResults.length === 1 ? "match found" : "matches found"}</span>
              <button
                type="button"
                className="as-mobile-dropdown-close"
                onClick={() => setSearchQuery("")}
              >
                Clear
              </button>
            </div>
            {searchResults.length > 0 ? (
              searchResults.map((res, i) => (
                <button
                  key={i}
                  type="button"
                  className="as-mobile-search-dropdown-item"
                  onClick={() => {
                    handleSearchResultClick(res.secIdx, res.subTitle);
                  }}
                >
                  <span className="as-mobile-dropdown-sub">{res.subTitle}</span>
                  <span className="as-mobile-dropdown-sec">{res.secTitle}</span>
                </button>
              ))
            ) : (
              <div className="as-mobile-search-dropdown-empty">
                No topics matching "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        className={`as-mobile-fab-pill ${mobileNavOpen ? "hidden" : ""}`}
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open Documentation Index"
      >
        <BookOpen className="w-4 h-4" />
        <span>Index</span>
      </button>

      {mobileNavOpen && (
        <div
          className="as-mobile-backdrop"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="as-layout-grid">
        <aside className={`as-sidebar ${mobileNavOpen ? "mobile-open" : ""}`} id="as-docs-sidebar">
          <div className="as-sidebar-top-bar">
            <div className="as-sidebar-title">
              <BookOpen className="w-4 h-4 text-[#4F1985]" />
              <span>Table of Contents</span>
            </div>
            <button
              type="button"
              className="as-mobile-drawer-close"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close Documentation Index"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
          </div>

          <div className="as-sidebar-search">
            <Search className="as-sidebar-search-icon" />
            <input
              type="text"
              placeholder="Filter topics, commands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="as-sidebar-search-input"
              aria-label="Filter documentation topics"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="as-sidebar-search-clear"
                title="Clear filter"
                type="button"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {searchQuery.trim() && (
            <div className="as-sidebar-search-results">
              <div className="as-sidebar-search-badge">
                <span>{searchResults.length} {searchResults.length === 1 ? "match found" : "matches found"}</span>
              </div>
              {searchResults.length > 0 ? (
                searchResults.map((res, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      handleSearchResultClick(res.secIdx, res.subTitle);
                      setMobileNavOpen(false);
                    }}
                    className="as-sidebar-search-item"
                    type="button"
                  >
                    <span className="as-sidebar-search-sub">{res.subTitle}</span>
                    <span className="as-sidebar-search-sec">{res.secTitle}</span>
                  </button>
                ))
              ) : (
                <div className="as-sidebar-search-empty">
                  No topics matching "{searchQuery}"
                </div>
              )}
            </div>
          )}

          <div className="as-nav-tree">
            {SECTIONS_CONFIG.map((sec, idx) => {
              const isSecActive = activeSectionIdx === idx;
              const isExpanded = !!openSections[idx];
              return (
                <div key={sec.id} className="as-nav-tree-section">
                  <button
                    type="button"
                    onClick={(e) => toggleSection(idx, e)}
                    className={`as-tree-cat-btn ${isSecActive ? "active" : ""}`}
                    aria-expanded={isExpanded}
                    title={isExpanded ? `Collapse ${sec.title}` : `Open ${sec.title}`}
                  >
                    <span className="as-tree-cat-text">{sec.title}</span>
                    <span className={`as-tree-cat-chevron-badge ${isExpanded ? "open" : ""}`}>
                      <ChevronDown
                        className={`as-tree-cat-chevron ${isExpanded ? "open" : ""}`}
                      />
                    </span>
                  </button>

                  {isExpanded && sec.subsections && (
                    <div className="as-subsections-tree">
                      {sec.subsections.map((sub) => {
                        const isSubActive = activeSub === sub;
                        return (
                          <button
                            key={sub}
                            type="button"
                            onClick={() => {
                              scrollToSub(sub, idx);
                              setMobileNavOpen(false);
                            }}
                            className={`as-tree-sub-btn ${isSubActive ? "active" : ""}`}
                          >
                            <span className="truncate">{sub}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="as-sidebar-meta">
            <a
              href={GITHUB_REPO_URL}
              target="_blank"
              rel="noreferrer"
              className="as-sidebar-link"
            >
              <ExternalLink className="w-3 h-3" />
              <span>GitHub Repository</span>
            </a>
            <a
              href="https://discord.gg/nGtFAR3hwn"
              target="_blank"
              rel="noreferrer"
              className="as-sidebar-link"
            >
              <ExternalLink className="w-3 h-3" />
              <span>Join Research Discord</span>
            </a>
          </div>
        </aside>

        <main className="as-content-surface" ref={mainContentRef}>
          <div className="as-content-body">
            {activeSectionIdx === 0 && (
            <section id="part-a" className="as-section-group">
              <div className="as-chapter-banner">
                <div className="as-chapter-banner-header">
                  <div className="as-chapter-badge-wrap">
                    <span className="as-chapter-badge">CHAPTER {SECTIONS_CONFIG[0].number}</span>
                    <span className="as-chapter-counter">Section 1 of {SECTIONS_CONFIG.length}</span>
                  </div>
                  <h2 className="as-chapter-title">{SECTIONS_CONFIG[0].title}</h2>
                  <p className="as-chapter-desc">{SECTIONS_CONFIG[0].desc}</p>
                </div>
              </div>

              <div>
                <div className="as-card-block" id={slugify("Hero & Positioning")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">01</span>
                    <span>A SCIENTIFIC AI ASSISTANT BY BOLTZMANN LABS</span>
                  </h3>
                  
                  <p className="as-text">
                    <em>Move from a research question to a tool-backed workflow.</em>
                  </p>
                  <p className="as-text">
                    Agent Scientist brings literature research, data analysis and approved scientific tools into one conversational workspace. Ask in plain language, connect the services you use, and keep each project organized with its own skills, sources and conversation history.
                  </p>
                  <p className="as-text">
                    From protein research to small-molecule analysis, work with a configurable assistant that can inspect inputs, use available tools and explain where evidence ends and a computational prediction begins.
                  </p>

                  <div className="as-grid-cards">
                    <div className="as-grid-card">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Self-hosted application</span>
                    </div>
                    <div className="as-grid-card">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Configurable models</span>
                    </div>
                    <div className="as-grid-card">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Scientific service connections</span>
                    </div>
                    <div className="as-grid-card">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Reusable skills</span>
                    </div>
                    <div className="as-grid-card">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Named research profiles</span>
                    </div>
                  </div>

                  <p className="as-text" style={{ marginTop: "1.25rem" }}>
                    <strong>Product introduction:</strong> Research rarely happens in one application. Papers live in literature databases, structures in public repositories, analysis in local files, and computational jobs in specialized services. Agent Scientist helps you work across those resources without treating every question as a new setup exercise.
                  </p>
                  <p className="as-text">
                    Start with a question, an approved file or a scientific objective. SCI uses the capabilities available in the active session, requests missing information, and returns an explanation with the artifacts or sources produced by the workflow. External services remain subject to their own credentials, availability and usage costs.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("The Problem & Research Friction")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">02</span>
                    <span>The Problem & Research Friction</span>
                  </h3>
                  <p className="as-text">
                    <em>Less setup friction. More research context.</em>
                  </p>
                  <p className="as-text">
                    A literature question becomes a file search. A structure analysis becomes a service login. A new project becomes another collection of scripts, notes and settings. General-purpose chat can explain an idea, but useful scientific execution also needs the right tools, prepared inputs and a clear record of what actually happened.
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Research friction</th>
                          <th>How SCI helps</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Tools are scattered</strong></td>
                          <td>Bring configured scientific services, MCP connections, approved plugins and local work into the same conversation.</td>
                        </tr>
                        <tr>
                          <td><strong>Project context gets mixed</strong></td>
                          <td>Use named profiles with separate conversations, selected capabilities, service settings and workspaces.</td>
                        </tr>
                        <tr>
                          <td><strong>Predictions sound like proof</strong></td>
                          <td>Ask for sources, inspect returned artifacts and distinguish computational outputs from experimental evidence.</td>
                        </tr>
                        <tr>
                          <td><strong>Work is hard to revisit</strong></td>
                          <td>Resume retained conversations and inspect stored tool activity without rerunning the original calls.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="as-card-block" id={slugify("How It Works (6-Step Workflow)")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">03</span>
                    <span>How It Works (6-Step Workflow)</span>
                  </h3>

                  <div className="as-step-list">
                    <div className="as-step-card">
                      <div className="as-step-badge">1</div>
                      <div className="as-step-info">
                        <strong>Describe the objective</strong>
                        <p>State the scientific question, available inputs, expected outputs and important limitations.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">2</div>
                      <div className="as-step-info">
                        <strong>Choose the project context</strong>
                        <p>Use main or activate a research profile with the appropriate skills and connections.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">3</div>
                      <div className="as-step-info">
                        <strong>Review requirements</strong>
                        <p>Confirm missing inputs, credentials, destinations, execution permissions and costs where required.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">4</div>
                      <div className="as-step-info">
                        <strong>Use the configured workflow</strong>
                        <p>SCI selects suitable available tools and follows the matching instructions or tool contract.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">5</div>
                      <div className="as-step-info">
                        <strong>Inspect the outcome</strong>
                        <p>Review job state, downloaded artifacts, counts, sources and limitations before treating the task as complete.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">6</div>
                      <div className="as-step-info">
                        <strong>Continue when ready</strong>
                        <p>Keep the conversation, return to another project, or resume the saved session later.</p>
                      </div>
                    </div>
                  </div>

                  <CalloutBox type="purple" title="THE PRINCIPLE">
                    Connected tools and persistent context support the research process. They do not replace experimental design, scientific judgment or independent validation.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("Capabilities")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">04</span>
                    <span>Capabilities</span>
                  </h3>
                  <p className="as-text">
                    <em>A workspace shaped around your science.</em>
                  </p>

                  <div className="as-grid-cards" style={{ gridTemplateColumns: "1fr" }}>
                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Scientific services</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Authorize the Boltzmann connector with your own API key, or connect ToolUniverse, PubMed and other compatible MCP services. Browse the scientific catalog by domain and module. Seeing a tool in a catalog does not mean that it is authorized or ready to execute.
                      </p>
                    </div>

                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Reusable scientific skills</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Load instructions for research, bioinformatics, chemistry, statistics, visualization and related work. Add prepared skill material or draft a reviewed skill from a workflow description. Skills guide execution; they are not scientific software, installed dependencies or evidence of correctness.
                      </p>
                    </div>

                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Research profiles</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Create separate projects for antibody research, enzyme analysis, omics or small-molecule work. Select relevant capabilities and service credentials, add optional sources, and switch between saved conversations. Supported model authentication can be shared while projects keep their own selected models and state.
                      </p>
                    </div>

                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Files and reference sources</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Work with downloaded files, project folders and public reference URLs. Choose how project sources may be read or sent to the selected model. Saving a source reference does not automatically read, upload or index its contents.
                      </p>
                    </div>

                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Models and persistent conversations</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Use a supported provider or a compatible custom endpoint. Return to saved sessions, review retained messages, and open tool details separately. Full visible history and the model's active context are different: long conversations may be compressed.
                      </p>
                    </div>

                    <div className="as-grid-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.35rem" }}>
                      <strong>Extensibility with clear boundaries</strong>
                      <p className="as-text" style={{ margin: 0, fontSize: "0.85rem", color: "#64748B" }}>
                        Request new tools, MCP services and reusable instructions through dedicated entry commands. Compatibility, installation, permissions and execution still require review. An ordinary website, repository or REST API is not automatically a ready-to-run integration.
                      </p>
                    </div>
                  </div>

                  <p className="as-text" style={{ marginTop: "1rem" }}>
                    <strong>Available surfaces:</strong> Core terminal CLI, with separately configured dashboard, messaging and desktop options. Frontend builds and native-package availability must be checked for the chosen deployment.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("Research Examples")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">05</span>
                    <span>Research Examples</span>
                  </h3>
                  <p className="as-text">
                    <em>Start with the question you already have.</em>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Research example</th>
                          <th>Illustrative request</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Literature review</strong></td>
                          <td>Find papers on a defined research question, cite the sources you actually retrieve, and separate established evidence from hypotheses.</td>
                        </tr>
                        <tr>
                          <td><strong>Structure analysis</strong></td>
                          <td>Use my approved structure file to summarize chain composition and structure-quality considerations. Explain any missing software or data.</td>
                        </tr>
                        <tr>
                          <td><strong>Enzyme workflow</strong></td>
                          <td>For my approved enzyme task, review the generation tool's required inputs, then predict solubility for the returned candidates if a compatible tool is available. Preserve candidate identifiers.</td>
                        </tr>
                        <tr>
                          <td><strong>Small-molecule work</strong></td>
                          <td>Review the paired compound and target inputs, identify a suitable configured analysis tool, and explain the output scale before interpreting scores.</td>
                        </tr>
                        <tr>
                          <td><strong>Project continuity</strong></td>
                          <td>Create an omics profile with the skills, selected services and reference sources I approve. Let me return to its saved conversation later.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    These are workflow examples, not promises that every service, file or scientific objective is supported. Tool access, input requirements and account entitlements are checked separately.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("Get Started & Repository")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">06</span>
                    <span>Get Started & Repository</span>
                  </h3>
                  <p className="as-text">
                    <em>Install SCI. Connect what you need. Build your research context.</em>
                  </p>
                  <p className="as-text">
                    Start from the Agent Scientist repository, complete model setup, and inspect your available capabilities. Add scientific services only when your task needs them. No model credits or scientific-service subscription are included with the source code.
                  </p>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.85rem", margin: "1.35rem 0" }}>
                    <a
                      href={GITHUB_REPO_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="as-footer-action-btn primary"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View GitHub Repository</span>
                    </a>
                    <button
                      onClick={() => scrollToSection("part-b", 1)}
                      className="as-footer-action-btn secondary"
                    >
                      <span>Read Product Guide</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* <CalloutBox type="purple" title="SUGGESTED FOOTER COPY">
                    Agent Scientist by Boltzmann Labs. A scientific AI assistant for connected research workflows. Computational results require scientific review; they are not experimental or clinical validation.
                    <br />
                    <em>Footer links: Repository / Installation / Privacy / Security / License. Publish an approved support contact and private security-reporting channel before external release.</em>
                  </CalloutBox> */}
                </div>
              </div>
              <div className="as-chapter-nav-inline only-next">
                <button
                  onClick={() => scrollToSection("part-b", 1)}
                  className="as-footer-action-btn primary"
                >
                  <span>Next: Part B: Product Guide</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
            )}

            {activeSectionIdx === 1 && (
            <section id="part-b" className="as-section-group">
              <div className="as-chapter-banner">
                <div className="as-chapter-banner-header">
                  <div className="as-chapter-badge-wrap">
                    <span className="as-chapter-badge">CHAPTER {SECTIONS_CONFIG[1].number}</span>
                    <span className="as-chapter-counter">Section 2 of {SECTIONS_CONFIG.length}</span>
                  </div>
                  <h2 className="as-chapter-title">{SECTIONS_CONFIG[1].title}</h2>
                  <p className="as-chapter-desc">{SECTIONS_CONFIG[1].desc}</p>
                </div>
              </div>

              <div>
                <div className="as-card-block" id={slugify("01 Linux & macOS Installation")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">01</span>
                    <span>Install and Launch SCI (Linux / macOS)</span>
                  </h3>
                  <p className="as-text">
                    Agent Scientist is a standalone application. A separate coding-agent installation is not required to run SCI. You do need access to a supported model provider or compatible endpoint. Optional scientific services require their own setup.
                  </p>
                  <p className="as-text">
                    <strong>Linux / macOS source installation:</strong> Use a normal user account, internet access and sufficient disk space. Required utilities include Git, Bash, curl, tar, awk and a SHA256 verifier. On Ubuntu, an administrator can install missing standard utilities with:
                  </p>

                  <CodeBlock
                    id="cmd-ubuntu-deps"
                    label="UBUNTU DEPENDENCY SETUP"
                    code="sudo apt update && sudo apt install -y git curl ca-certificates tar gawk coreutils"
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    <strong>Clone and install without sudo:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-linux-install"
                    label="LINUX / MACOS SOURCE INSTALL"
                    code={`git clone https://github.com/boltzmannlabs/Agent-Scientist.git\ncd Agent-Scientist\nbash ./install-sci.sh\nexport PATH="$HOME/.local/bin:$PATH"\nagent-sci`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>What it does</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>git clone</strong></td>
                          <td>Downloads the application source from the approved repository.</td>
                        </tr>
                        <tr>
                          <td><strong>cd Agent-Scientist</strong></td>
                          <td>Enters the checkout so its installer and supporting files can be found.</td>
                        </tr>
                        <tr>
                          <td><strong>install-sci.sh</strong></td>
                          <td>Prepares the managed runtime, locked application dependencies, launchers and bundled skills. Interactive installation opens provider setup.</td>
                        </tr>
                        <tr>
                          <td><strong>export PATH</strong></td>
                          <td>Makes the user-local launcher available in the current terminal. A new terminal can pick up the installed shell configuration.</td>
                        </tr>
                        <tr>
                          <td><strong>agent-sci</strong></td>
                          <td>Starts the interactive assistant.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <CalloutBox type="purple" title="KEEP THE CHECKOUT">
                    The launchers refer to the installed source location. If you move the checkout, reinstall from its new location. Do not install application packages into system Python as a workaround.
                    <br /><br />
                    The core installer does not include scientific model weights, datasets, Docker images, GPUs or every optional tool dependency. Setup time and disk requirements depend on the network, cache and optional components; no fixed duration is promised.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("02 Windows & First Use")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">02</span>
                    <span>Verify Your Installation (Windows & First Use)</span>
                  </h3>
                  <p className="as-text">
                    <strong>Windows source installation:</strong> Install Git for Windows and use a normal PowerShell window. From your chosen installation folder:
                  </p>

                  <CodeBlock
                    id="cmd-win-install"
                    label="WINDOWS POWERSHELL INSTALLATION"
                    code={`git clone https://github.com/boltzmannlabs/Agent-Scientist.git\ncd Agent-Scientist\n.\\install-sci.ps1`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Complete provider setup, open a new terminal, and run <code>agent-sci</code>. If PowerShell blocks the local script, review it and follow your organization's execution policy; do not bypass centrally enforced controls. See <code>INSTALLATION.md</code> for the process-only override and PATH recovery options.
                  </p>

                  <p className="as-text">
                    <strong>Unattended Linux/server installation:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-server-install"
                    label="UNATTENDED SERVER INSTALLATION"
                    code={`bash ./install-sci.sh --non-interactive\nexport PATH="$HOME/.local/bin:$PATH"\nsci setup\nagent-sci`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Run these from the complete checkout. Non-interactive installation prepares software only; run <code>sci setup</code> later in an interactive terminal to select and authenticate the model provider.
                  </p>

                  <p className="as-text">
                    <strong>Check the core CLI:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-core-cli"
                    label="CLI HEALTH CHECK"
                    code={`sci --help\nsci doctor\nagent-sci`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <div className="as-step-list">
                    <div className="as-step-card">
                      <div className="as-step-badge">1</div>
                      <div className="as-step-info">
                        <strong>Verify help menu</strong>
                        <p>Inside SCI, enter <code>/help</code> and confirm that the command list appears.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">2</div>
                      <div className="as-step-info">
                        <strong>Test model communication</strong>
                        <p>Send a simple non-sensitive question to verify model access.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">3</div>
                      <div className="as-step-info">
                        <strong>Inspect inventory</strong>
                        <p>Enter <code>/capabilities</code> to inspect enabled capabilities and setup requirements without running scientific jobs.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">4</div>
                      <div className="as-step-info">
                        <strong>Add connections as needed</strong>
                        <p>Configure scientific services only as needed, then start a fresh session for deferred additions.</p>
                      </div>
                    </div>
                  </div>

                  <CalloutBox type="green" title="INSTALLATION IS NOT SERVICE READINESS">
                    A successful chat confirms model access, not Boltzmann authentication, MCP connectivity, missing scientific dependencies or scientific correctness.
                    <br /><br />
                    Source-install instructions are separate from signed desktop/native packages. Do not describe Windows or macOS binaries as released or qualified until their actual artifacts and signing have been verified.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("03 Models & Authentication")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">03</span>
                    <span>Use Your Chosen Model & Authentication</span>
                  </h3>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Where</th>
                          <th>Command and purpose</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Shell</strong></td>
                          <td><code>sci setup</code> — initial configuration and provider setup.</td>
                        </tr>
                        <tr>
                          <td><strong>Shell</strong></td>
                          <td><code>sci model</code> — review or add model providers and saved defaults.</td>
                        </tr>
                        <tr>
                          <td><strong>Inside SCI</strong></td>
                          <td><code>/login</code> — open the SCI model setup menu; this is not a separate product login portal.</td>
                        </tr>
                        <tr>
                          <td><strong>Inside SCI</strong></td>
                          <td><code>/model</code> — explicitly review or switch the current conversation's model.</td>
                        </tr>
                        <tr>
                          <td><strong>Shell</strong></td>
                          <td><code>sci auth add openai-codex --type oauth</code> — review supported Codex credential reuse or sign-in.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text">
                    <strong>Shared availability, separate choices:</strong> Providers added through supported model/auth setup become available to main and named profiles. Each profile keeps its selected model. Adding a provider does not automatically switch every existing conversation to it.
                  </p>
                  <p className="as-text">
                    Global provider removal reviews affected saved defaults and auxiliary routes. Choose replacements or explicitly leave those routes unconfigured. Removal of provider availability is not necessarily logout or deletion of stored credentials.
                  </p>
                  <p className="as-text">
                    <strong>Reusing a current Codex login:</strong> SCI can validate and follow a supported current file-backed Codex login rather than maintaining stale copies of rotating tokens. The source can be the configured <code>CODEX_HOME</code> or the current user's normal Codex credential location. Codex owns refresh of a referenced file login.
                  </p>
                  <p className="as-text">
                    Do not promise credential reuse for every storage method or machine. Keyring, auto and ephemeral stores have additional limits. Missing, expired, invalidated or unsupported credentials require the recovery path shown by setup; an invalid token cannot be made valid merely by choosing it again. Never copy another user's credential files.
                  </p>
                  <p className="as-text">
                    <strong>Thinking display is not model reasoning:</strong> The default display hides thinking text with <code>display.show_reasoning: false</code>. This controls visibility, not whether the model reasons internally. Reasoning effort is a separate provider/model setting, and supported levels vary.
                  </p>

                  <CalloutBox type="purple" title="SESSION SAFETY">
                    Saved setup defaults normally apply to new sessions. Explicit model switching can change the model-side cache. Do not silently switch providers after an authentication failure.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("04 Scientific Connections")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">04</span>
                    <span>Connect the Services You Need</span>
                  </h3>
                  <p className="as-text">
                    <strong>Boltzmann:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-boltzmann"
                    label="BOLTZMANN CONNECTOR COMMANDS"
                    code={`/add_boltzmann\n/boltzmann_tools\n/boltzmann_tools search docking`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    The first command opens masked key entry in the active profile. Setup performs a read-only authentication check; it does not submit a scientific job. A validated key and profile-specific authorization record are required. Restart after successful setup for normal next-session activation.
                  </p>
                  <p className="as-text">
                    The scientific catalog is grouped by domain, module and tool name. Domains include <strong>Protein Engineering</strong>, <strong>Small Molecule Design</strong>, <strong>Molecular Dynamics</strong>, <strong>Synthetic Chemistry</strong>, <strong>RNA Design</strong> and <strong>Density Functional Theory</strong>. Catalog coverage is not a statement of live account entitlement or end-to-end verification.
                  </p>
                  <p className="as-text">
                    Five connector operations handle upload, submit, status, download and diagnostic-log retrieval. These are transport/job-management helpers, not the scientific workflows shown to researchers. The matching installed contract defines each scientific request.
                  </p>

                  <p className="as-text">
                    <strong>ToolUniverse and PubMed:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-capabilities-setup"
                    label="STANDARD MCP CAPABILITIES SETUP"
                    code="/capabilities setup"
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Review the offered connection definition, destination and requirements. ToolUniverse can require an approved managed dependency installation and separate approval to launch its local server. The included PubMed option uses a third-party MCP endpoint, not an NCBI-operated service. Nothing is connected merely by cloning SCI.
                  </p>
                  <p className="as-text">
                    After approved handshake/discovery, explicitly choose tools and save. An MCP allowlist does not automatically include future server tools. ToolUniverse's <code>execute_tool</code> is a dispatcher: granting it is broader than selecting one fixed scientific function from its internal catalog.
                  </p>

                  <p className="as-text">
                    <strong>Other MCP services:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-add-mcp"
                    label="CONNECT CUSTOM MCP SERVICE"
                    code="/Add_mcp Connect this documented MCP service: <endpoint-or-configuration>"
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    This passes the request into the normal agent workflow. Review authentication, local execution, installation, permissions and data transfer as applicable. An ordinary webpage or REST URL is not automatically an MCP endpoint. A failed test or missing authorization must not be reported as a usable connection.
                  </p>
                  <p className="as-text">
                    Connection checks establish discovery/connectivity only; they do not establish scientific validity, paid-job success or support for every advertised workflow.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("05 Skills & Tool Additions")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">05</span>
                    <span>Extend Without Guessing</span>
                  </h3>
                  <p className="as-text">
                    <strong>Add a skill:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-add-skill"
                    label="ADD REUSABLE SCIENTIFIC SKILL"
                    code={`/Add_skill\n/Add_skill "/absolute/path/to/prepared-skill"`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Provide prepared skill material, a public link, a local file/folder, or a workflow description. Link/path requests go to the agent; the <em>Describe a workflow</em> route provides guided drafting. For a draft, explain its purpose, inputs, outputs and limitations instead of asking the model to invent missing scientific requirements.
                  </p>
                  <p className="as-text">
                    Review the resulting instructions before saving. Where model-assisted drafting is used, review the actual model, sources and outbound text before consent. Approved additions normally become available in the next session. Security review is not a check of scientific correctness.
                  </p>

                  <p className="as-text">
                    <strong>Request a tool integration:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-add-tool"
                    label="REQUEST TOOL INTEGRATION"
                    code="/Add_tool Add this tool: <official-repository-or-tool-URL>"
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    The command forwards your request to SCI's normal agent rather than a separate fixed setup wizard. Describe the tool and intended use, then review what the agent proposes. A GitHub repository may contain a compatible plugin, an MCP service, an ordinary program, or only source code. Its URL alone does not prove compatibility.
                  </p>
                  <p className="as-text">
                    For an installed local program, provide its resolved location, documented invocation, expected inputs/outputs, runtime and limitations. A guided skill can explain how to use existing software through the terminal; that is not the same as registering a new model function. Missing dependencies require a reviewed setup plan, not guessed scientific parameters.
                  </p>

                  <p className="as-text">
                    <strong>Read the capability state accurately:</strong>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>State</th>
                          <th>Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Installed/configured</strong></td>
                          <td>Files or a connection definition exist; readiness may still be unverified.</td>
                        </tr>
                        <tr>
                          <td><strong>Enabled for profile</strong></td>
                          <td>The profile permits the capability, subject to authorization and runtime checks.</td>
                        </tr>
                        <tr>
                          <td><strong>Available now</strong></td>
                          <td>The current conversation exposes the capability, possibly through discovery.</td>
                        </tr>
                        <tr>
                          <td><strong>Pending next session</strong></td>
                          <td>Saved changes have not replaced the live conversation's tools or instructions.</td>
                        </tr>
                        <tr>
                          <td><strong>Setup required</strong></td>
                          <td>Authentication, dependencies or connection readiness are missing or unverified.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    Use <code>/skills</code> for skill management and <code>/capabilities</code> for the combined inventory. Do not paste keys into an addition request.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("06 Create a Research Profile")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">06</span>
                    <span>Build a Project Context (Create Profile)</span>
                  </h3>

                  <CodeBlock
                    id="cmd-create-profile"
                    label="CREATE NATIVE RESEARCH PROFILE"
                    code="/Create_profile"
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    The flow is <strong>Name → Scientific skills → Tools → Credentials → Optional sources → Review → Create</strong>. It creates a native SCI profile, not a second project database.
                  </p>

                  <div className="as-step-list">
                    <div className="as-step-card">
                      <div className="as-step-badge">1</div>
                      <div className="as-step-info">
                        <strong>Name the profile</strong>
                        <p>Use a unique descriptive name such as omics or enzyme-project.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">2</div>
                      <div className="as-step-info">
                        <strong>Select scientific skills</strong>
                        <p>Review the descriptions and choose the instructions relevant to this project.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">3</div>
                      <div className="as-step-info">
                        <strong>Select tools and integrations</strong>
                        <p>Review MCP tool permissions and plugin-wide access. ToolUniverse and Boltzmann appear first when available.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">4</div>
                      <div className="as-step-info">
                        <strong>Review credentials</strong>
                        <p>For supported services, choose an available existing key or enter a new masked key. Required authentication and external checks still apply.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">5</div>
                      <div className="as-step-info">
                        <strong>Add optional sources</strong>
                        <p>Supply a file, folder or public reference URL and review its access mode.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">6</div>
                      <div className="as-step-info">
                        <strong>Review the summary</strong>
                        <p>Confirm capabilities, hidden credential choices, backend, workspace and source access, then create the profile.</p>
                      </div>
                    </div>
                    <div className="as-step-card">
                      <div className="as-step-badge">7</div>
                      <div className="as-step-info">
                        <strong>Activate now or stay</strong>
                        <p>Switching profiles is explicit; creating one does not silently move ongoing work.</p>
                      </div>
                    </div>
                  </div>

                  <p className="as-text">
                    <strong>What is separate:</strong> Profiles keep separate conversations, memory, settings, selected capabilities, service credentials, workspaces and persona state. Supported LLM-provider authentication can be shared. Main's selected skills and project settings are not replaced by another profile's choices.
                  </p>
                  <p className="as-text">
                    <strong>Keys and execution environments:</strong> Reusing a service key means an approved copy in the new profile's private storage, not a live link to main. Changing one copy does not update the others; service-side revocation affects every copy of that key. Unsupported credentialed integrations require their native setup rather than invented validation.
                  </p>
                  <p className="as-text">
                    New profiles reuse supported local software or a reviewed Docker image with separate workspaces. No normal <code>--prepare</code> step is needed. Local execution is not an OS sandbox. Docker image downloads require approval; remote backends need separate workspace and credential handling and are not automatically cloned by the creation wizard.
                  </p>

                  <CalloutBox type="purple" title="SOURCES ARE NOT UPLOADS">
                    Choose reference-only, approved local reading/processing, or relevant content to the selected model. Creation itself does not read, download, upload or index source contents.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("07 Update & Activate")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">07</span>
                    <span>Switch Projects, Not Identities (Update & Activate)</span>
                  </h3>
                  <p className="as-text">
                    <strong>Review an existing profile:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-update-profile"
                    label="UPDATE PROFILE CONFIGURATION"
                    code={`/Update_profile\n/Update_profile "omics"`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Existing selections are preselected. Review science skills, MCP connections, plugins, tool permissions, required credentials and optional sources. Unchanged credentials and custom entries are preserved. Deselecting disables profile access; it does not uninstall shared software.
                  </p>
                  <p className="as-text">
                    Skills, MCP connections, plugins and discovered MCP tool lists have independent <em>Select all</em>, <em>Clear selection</em> and <em>Done</em> controls. Select all covers the whole section, including filtered-out results. You can deselect individual items afterward. These controls do not bypass authentication, execution approval or missing-dependency checks.
                  </p>
                  <p className="as-text">
                    Review the change summary and save for the next session. The configuration update checks for concurrent edits. The wider operation is not a guaranteed multi-file rollback: cancellation or failure can leave approved downloads, copied files or external grants, which must be reported.
                  </p>

                  <p className="as-text">
                    <strong>List and activate:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-activate"
                    label="LIST & ACTIVATE PROFILES"
                    code={`/profiles_list\n/activate\n/activate "omics"\n/activate main`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    <code>/profiles_list</code> uses the same registry as <code>sci profile list</code>. Bare <code>/activate</code> opens a picker; a supplied name switches directly. The left status-bar label shows the active profile. After normal shutdown, the destination's native CLI resumes its latest saved CLI conversation when available, or starts a new one.
                  </p>
                  <p className="as-text">
                    Slash commands run in the active profile too: authorizing Boltzmann or adding a skill must not accidentally modify main. Finish or explicitly cancel running work before switching. A failed conversation save should leave you in the departing profile, not discard its state.
                  </p>
                  <p className="as-text">
                    <strong>Isolation and existing projects:</strong> Existing restricted profiles retain their saved permission and execution boundaries. Activation is not silent migration or permission expansion. Review any required migration separately. Do not assume automatic routing or multi-profile collaboration is enabled merely because profiles exist; those behaviors require their own reviewed configuration and permission boundaries.
                  </p>

                  <CalloutBox type="purple" title="ISOLATION BOUNDARIES">
                    Profile separation is an application boundary, not a substitute for OS users, filesystem permissions, container policy or service-side authorization.
                  </CalloutBox>
                </div>

                <div className="as-card-block" id={slugify("08 Conversations & Resume")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">08</span>
                    <span>Return to the Whole Conversation</span>
                  </h3>

                  <CodeBlock
                    id="cmd-resume"
                    label="CONVERSATION RESUME COMMANDS"
                    code={`/resume\n/sessions\n/resume <session-id>`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    The active profile's conversation picker supports search and pagination, with titles, activity, message counts and session identifiers. Choose the saved conversation you want; do not rely on remembering a long ID.
                  </p>
                  <p className="as-text">
                    <strong>What appears after resume:</strong> Retained user messages and agent replies are restored to terminal scrollback, rather than only the most recent exchange. The normal chat view omits the extra tool-call/result stream. Continue at the bottom as in an ordinary conversation.
                  </p>
                  <p className="as-text">
                    Press <strong>Ctrl+O</strong> or <strong>F8</strong> for the transcript viewer. Browse or search retained activity; select a tool entry with Tab and press Enter to expand its stored arguments or result. Escape returns to the unchanged prompt. Viewing a saved call does not rerun it. There is no separate <code>/history</code> command.
                  </p>
                  <p className="as-text">
                    <strong>Stored history is not the active context window:</strong> Session storage retains structured messages, tool activity and metadata. The model still has a finite context window, and long conversations can use compression and linked continuation history. Displaying the retained transcript does not blindly resend all historical tokens or replace the cached system prompt.
                  </p>
                  <p className="as-text">
                    Deleted, never-recorded or unavailable history cannot be reconstructed. Terminal scrollback capacity also affects how far you can scroll. Use the viewer for retained details. Reasoning display remains hidden by default, and redaction is a safeguard rather than perfect secret detection.
                  </p>

                  <p className="as-text">
                    <strong>Useful session commands:</strong>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Command</th>
                          <th>Use</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>/new</strong></td>
                          <td>Start a fresh conversation in the active profile.</td>
                        </tr>
                        <tr>
                          <td><strong>/title &lt;name&gt;</strong></td>
                          <td>Give the session a useful title for later discovery.</td>
                        </tr>
                        <tr>
                          <td><strong>/status</strong></td>
                          <td>Inspect current session/model/context information.</td>
                        </tr>
                        <tr>
                          <td><strong>/save md &lt;filename&gt; redact</strong></td>
                          <td>Export conversation text using the supported redaction option; inspect before sharing.</td>
                        </tr>
                        <tr>
                          <td><strong>/exit</strong></td>
                          <td>End the interactive CLI normally.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    From the shell, <code>sci --continue</code> and <code>sci --resume &lt;session-id&gt;</code> provide additional supported resume routes. Resuming never reinstates revoked service permissions.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("09 Scientific Execution")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">09</span>
                    <span>Know What Ran—and What Did Not</span>
                  </h3>

                  <CalloutBox type="purple" title="A STRUCTURED RESEARCH REQUEST">
                    Objective: analyze my approved enzyme candidates. Inputs: the local FASTA/CSV I provide. Use an authorized matching scientific tool, preserve candidate IDs, and produce a CSV with raw predictions, the documented output meaning and limitations. Before any upload or paid submission, confirm the destination, required settings and cost information available. Do not invent missing values or repeat an ambiguous submission.
                  </CalloutBox>

                  <p className="as-text">
                    <strong>Execution policy:</strong>
                  </p>
                  <ul className="as-text" style={{ paddingLeft: "1.25rem", margin: "0.5rem 0 1.25rem 0" }}>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Check the tools available in the current session first. When an authorized verified Boltzmann workflow matches the request, load its matching contract and use the native connector.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Otherwise prefer suitable available scientific tools. Skills can guide tool selection, input preparation and output validation without requiring local scripts.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Use local instructions/scripts only when no suitable tool exists or local execution is explicitly requested. Explain the fallback and respect the configured execution backend.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Do not silently switch providers after an authentication failure. Never duplicate a submitted, pending or ambiguously accepted job.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Verify actual returned files, candidate IDs and counts before reporting completion. A successful HTTP response or finished job is not scientific validation.
                    </li>
                  </ul>

                  <p className="as-text">
                    <strong>Interpret job states honestly:</strong>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Observed state</th>
                          <th>Correct interpretation</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Blocked before submission</strong></td>
                          <td>No scientific request was sent if the connector confirms that stage; fix inputs or authorization first.</td>
                        </tr>
                        <tr>
                          <td><strong>Accepted with job ID</strong></td>
                          <td>Save the identifier and poll that job; acceptance is not completion.</td>
                        </tr>
                        <tr>
                          <td><strong>Pending/running</strong></td>
                          <td>Work has not completed; do not start a duplicate to get a faster answer.</td>
                        </tr>
                        <tr>
                          <td><strong>Error without acceptance evidence</strong></td>
                          <td>Outcome may be uncertain. Reconcile the existing attempt before retrying.</td>
                        </tr>
                        <tr>
                          <td><strong>Completed with artifacts</strong></td>
                          <td>Download and inspect artifacts, counts and output semantics before summarizing.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    Validation can be partial. Report unchecked chemistry, structure identity, evidence or runtime assumptions. Authorized best-effort use does not bypass malformed input, unsafe archives or missing service access.
                  </p> */}
                </div>

                <div className="as-card-block" id={slugify("10 Data, Privacy & Architecture")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">10</span>
                    <span>Local State. Explicit Destinations.</span>
                  </h3>
                  <p className="as-text">
                    SCI's configured home stores application state. The ordinary default is <code>~/.sci</code>, or the current user's <code>.sci</code> directory on Windows. <code>SCI_HOME</code> selects another state directory; changing it is not a migration. Profiles and execution backends can use additional workspace locations.
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>config.yaml / SOUL.md</strong></td>
                          <td>Settings and persona instructions.</td>
                        </tr>
                        <tr>
                          <td><strong>Credential storage / .env</strong></td>
                          <td>Saved authentication and secrets; not reusable public configuration.</td>
                        </tr>
                        <tr>
                          <td><strong>state.db and session records</strong></td>
                          <td>Persisted conversations, metadata and retained tool activity.</td>
                        </tr>
                        <tr>
                          <td><strong>Skills / plugins / MCPs</strong></td>
                          <td>Instructions, installed integrations and service connections; these are different capability types.</td>
                        </tr>
                        <tr>
                          <td><strong>Profiles / workspaces</strong></td>
                          <td>Project-specific state, permissions, inputs and outputs.</td>
                        </tr>
                        <tr>
                          <td><strong>Logs / caches / backups</strong></td>
                          <td>Diagnostics, cached material and recovery copies that can also contain sensitive data.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text">
                    <strong>Where information can go:</strong> A self-hosted application is not automatically offline. The selected model can receive conversation context, relevant file contents and tool results. Search/browser services receive their queries and requests. MCPs, plugins, Boltzmann services and remote execution backends receive the material needed for approved operations. Provider agreements govern external retention and processing.
                  </p>
                  <p className="as-text">
                    Source access modes guide use; local-backend instructions are not an OS-enforced data-loss-prevention system. A terminal container does not automatically sandbox trusted host plugins or every local MCP process. A configured approval policy can permit operations without a separate prompt for each one.
                  </p>
                  <p className="as-text">
                    <strong>Research data precautions:</strong>
                  </p>
                  <ul className="as-text" style={{ paddingLeft: "1.25rem", margin: "0.5rem 0 1.25rem 0" }}>
                    <li style={{ marginBottom: "0.4rem" }}>Use public, synthetic or approved de-identified fixtures during initial testing.</li>
                    <li style={{ marginBottom: "0.4rem" }}>Do not send patient records, private sequences, unpublished datasets or proprietary material without authorization and approved processing arrangements.</li>
                    <li style={{ marginBottom: "0.4rem" }}>Keep credentials out of prompts, reusable skills, repositories, screenshots, logs shared publicly and example commands.</li>
                    <li style={{ marginBottom: "0.4rem" }}>Treat logs, exports, memories, caches and backups as sensitive; redaction is not perfect and local storage is not a promise of encryption at rest.</li>
                    <li style={{ marginBottom: "0.4rem" }}>Disabling shared metrics does not disable ordinary model, tool, update or independently configured plugin traffic.</li>
                  </ul>

                  {/* <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    The organization must define its own retention, access-control, privacy and incident-reporting policies. SCI is not a certification of regulatory compliance or clinical suitability.
                  </p> */}
                </div>

                <div className="as-card-block" id={slugify("11 Interfaces, Updates & Recovery")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">11</span>
                    <span>Maintain a Working Installation</span>
                  </h3>
                  <p className="as-text">
                    <strong>Optional dashboard and desktop:</strong> The core installer prepares the CLI. Build frontend surfaces separately from the same checkout. These are source-build instructions, not evidence of published native packages.
                  </p>

                  <CodeBlock
                    id="cmd-dashboard"
                    label="OPTIONAL WEB DASHBOARD BUILD"
                    code={`npm ci\nnpm run build --workspace web\nsci dashboard`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    For the optional Electron source build:
                  </p>

                  <CodeBlock
                    id="cmd-electron"
                    label="ELECTRON DESKTOP BUILD"
                    code={`npm run build --workspace apps/desktop\nnpm run start --workspace apps/desktop`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Use the actual dashboard for the selected deployment; do not assume the reference product's experiment graph or benchmark panels exist here. Keep web access on localhost unless authentication, secure transport and network controls have been deliberately configured. Headless servers can use the CLI; messaging surfaces require their own platform setup.
                  </p>
                  <p className="as-text">
                    <strong>Source updates:</strong>
                  </p>

                  <CodeBlock
                    id="cmd-update"
                    label="CHECK & RUN SOURCE UPDATES"
                    code={`sci update --check\nsci update`}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />

                  <p className="as-text">
                    Inside SCI, <code>/update</code> exits the session and starts the updater. Startup recommendations normally use a 24-hour cache and do not install automatically. Source installs follow the approved repository's main branch. Keep your own backups and resolve local source edits before updating; restart afterward.
                  </p>
                  <p className="as-text">
                    The public version format is <strong>v1.M.m.p: Major.Milestone.Minor.Patch</strong>. The initial baseline is <code>v1.0.0.0</code>. Source tags, GitHub Releases, signed native installers and desktop update feeds are separate publication steps. Never move an already published release tag.
                  </p>
                  <p className="as-text">
                    <strong>Recovery without deleting state:</strong> If installation stops, retain the named failing stage and rerun the same installer after fixing the cause. If model access fails, review <code>sci model</code> or <code>/login</code>. If a service fails, inspect <code>/capabilities</code> and its authorization/dependency state. Do not erase <code>.sci</code>, silently substitute another backend or reinstall into system Python as a shortcut.
                  </p>
                  <p className="as-text">
                    Use <code>sci doctor</code> for diagnostics and <code>sci logs --help</code> for supported log-viewing options. Review and redact diagnostic material before sharing it.
                  </p>
                </div>

                <div className="as-card-block" id={slugify("12 Quick Reference & Troubleshooting")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">12</span>
                    <span>The Commands You Will Use Most & Troubleshooting</span>
                  </h3>

                  <p className="as-text">
                    <strong>Inside SCI commands reference:</strong>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Inside SCI</th>
                          <th>Purpose</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>/help</strong></td>
                          <td>Available commands and help.</td>
                        </tr>
                        <tr>
                          <td><strong>/login / /model</strong></td>
                          <td>Provider setup / explicit model selection.</td>
                        </tr>
                        <tr>
                          <td><strong>/capabilities [search text]</strong></td>
                          <td>Combined local inventory and setup state.</td>
                        </tr>
                        <tr>
                          <td><strong>/capabilities setup</strong></td>
                          <td>Review optional standard MCP connections.</td>
                        </tr>
                        <tr>
                          <td><strong>/add_boltzmann</strong></td>
                          <td>Authorize this profile with a masked key.</td>
                        </tr>
                        <tr>
                          <td><strong>/boltzmann_tools [domain]</strong></td>
                          <td>Browse scientific tools; use search &lt;text&gt; to filter.</td>
                        </tr>
                        <tr>
                          <td><strong>/Add_skill</strong></td>
                          <td>Provide skill material or describe a reviewed workflow.</td>
                        </tr>
                        <tr>
                          <td><strong>/Add_tool &lt;request or URL&gt;</strong></td>
                          <td>Ask the normal agent to add a tool.</td>
                        </tr>
                        <tr>
                          <td><strong>/Add_mcp &lt;request or endpoint&gt;</strong></td>
                          <td>Ask the normal agent to connect an MCP service.</td>
                        </tr>
                        <tr>
                          <td><strong>/Create_profile / /Update_profile</strong></td>
                          <td>Create or review a native research profile.</td>
                        </tr>
                        <tr>
                          <td><strong>/profiles_list / /activate [name]</strong></td>
                          <td>List profiles / choose or switch profile.</td>
                        </tr>
                        <tr>
                          <td><strong>/resume / /sessions</strong></td>
                          <td>Find and resume saved conversations.</td>
                        </tr>
                        <tr>
                          <td><strong>/new / /exit</strong></td>
                          <td>Start fresh / leave the CLI.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text" style={{ marginTop: "1.5rem" }}>
                    <strong>Symptom vs Next step:</strong>
                  </p>

                  <div className="as-table-box">
                    <table className="as-data-table">
                      <thead>
                        <tr>
                          <th>Symptom</th>
                          <th>Next step</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Launcher not found</strong></td>
                          <td>Open a new terminal or apply the documented PATH setting.</td>
                        </tr>
                        <tr>
                          <td><strong>401 or invalid login</strong></td>
                          <td>Revalidate the current credential source or sign in through supported setup. Do not keep retrying a rejected token.</td>
                        </tr>
                        <tr>
                          <td><strong>A new tool is missing</strong></td>
                          <td>Check profile authorization and next-session activation. Restart; do not assume the live toolset was refreshed.</td>
                        </tr>
                        <tr>
                          <td><strong>Only connector helpers appear</strong></td>
                          <td>Use <code>/boltzmann_tools</code> for the scientific catalog; verify the installed plugin/catalog and active profile.</td>
                        </tr>
                        <tr>
                          <td><strong>Missing scientific software</strong></td>
                          <td>Review the tool's runtime requirements. A skill alone does not install the software.</td>
                        </tr>
                        <tr>
                          <td><strong>A job timed out or has no ID</strong></td>
                          <td>Stop and reconcile acceptance before another submission. A frontend timeout is not proof of cancellation.</td>
                        </tr>
                        <tr>
                          <td><strong>History looks incomplete</strong></td>
                          <td>Choose the correct profile/session and inspect retained history in Ctrl+O / F8. Deleted history cannot be recreated.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="as-text" style={{ fontSize: "0.85rem", color: "#64748B" }}>
                    Shell commands run outside the assistant; slash commands run inside it. Setup and scientific operations can have charges or retained side effects. Read the reported outcome before retrying.
                  </p>
                </div>
              </div>
              <div className="as-chapter-nav-inline">
                <button
                  onClick={() => scrollToSection("part-a", 0)}
                  className="as-footer-action-btn secondary"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous: Part A: Overview</span>
                </button>
                <button
                  onClick={() => scrollToSection("part-c", 2)}
                  className="as-footer-action-btn primary"
                >
                  <span>Next: Part C: Editorial Notes &amp; FAQ</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
            )}

            {activeSectionIdx === 2 && (
            <section id="part-c" className="as-section-group">
              <div className="as-chapter-banner">
                <div className="as-chapter-banner-header">
                  <div className="as-chapter-badge-wrap">
                    <span className="as-chapter-badge">CHAPTER {SECTIONS_CONFIG[2].number}</span>
                    <span className="as-chapter-counter">Section 3 of {SECTIONS_CONFIG.length}</span>
                  </div>
                  <h2 className="as-chapter-title">{SECTIONS_CONFIG[2].title}</h2>
                  <p className="as-chapter-desc">{SECTIONS_CONFIG[2].desc}</p>
                </div>
              </div>

              <div>
                <div className="as-card-block" id={slugify("Frequently Asked Questions")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">?</span>
                    <span>Frequently Asked Questions</span>
                  </h3>
                  <p className="as-text">
                    <em>Publish what the product can prove.</em>
                  </p>

                  <div className="as-faq-group">
                    {[
                      {
                        q: "Does everything stay on my machine?",
                        a: "No. State is locally managed, but configured model providers, search services, scientific APIs, MCPs and remote backends can receive relevant data. Review each destination and agreement.",
                      },
                      {
                        q: "Does cloning the repository activate scientific tools?",
                        a: "No. The code and portable definitions can be distributed; personal credentials and subscriptions are not. Complete the selected service's setup, permissions and next-session activation.",
                      },
                      {
                        q: "Can I connect any GitHub project or website?",
                        a: "You can ask SCI to inspect it. Supported integration, runtime preparation and execution remain separate questions; arbitrary URLs are not automatically working tools.",
                      },
                      {
                        q: "Does a successful workflow validate the science?",
                        a: "No. Check output semantics, evidence, counts and artifacts. Predictions, geometry checks and generated candidates require independent scientific assessment.",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`as-faq-item ${openFaq === idx ? "open" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                          className="as-faq-btn as-faq-trigger"
                          aria-expanded={openFaq === idx}
                        >
                          <span className="as-faq-question">{item.q}</span>
                          <span className={`as-faq-icon-wrap ${openFaq === idx ? "open" : ""}`}>
                            <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                          </span>
                        </button>
                        {openFaq === idx && (
                          <div className="as-faq-text as-faq-content">{item.a}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="as-card-block" id={slugify("Publication Checklist")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">✓</span>
                    <span>Publication Checklist</span>
                  </h3>

                  <ul className="as-text" style={{ paddingLeft: "1.25rem", margin: "0.5rem 0 1.25rem 0" }}>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Use Agent Scientist and Boltzmann Labs consistently. Retain legally required license and third-party notices in the distribution.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Use real SCI screenshots without credentials, private paths, patient data or unpublished research. Do not reuse unrelated product screenshots or artwork.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Do not reuse the reference document's benchmark scores, experiment-DAG claims or host-plugin commands. Publish SCI benchmarks only with an approved evaluation and reproducible evidence.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Confirm the current release, supported platforms, live service readiness and native-package availability before publishing availability claims.
                    </li>
                    <li style={{ marginBottom: "0.5rem" }}>
                      Publish approved privacy, support and private security-reporting contacts. Do not invent addresses, compliance claims, pricing, customer endorsements or uptime promises.
                    </li>
                  </ul>
                </div>

                {/* <div className="as-card-block" id={slugify("Source Basis")}>
                  <h3 className="as-h3">
                    <span className="as-h3-tag">§</span>
                    <span>Source Basis & Boundaries</span>
                  </h3>
                  <p className="as-text">
                    Prepared from the supplied document's structure and the current SCI repository: <code>README.md</code>; <code>INSTALLATION.md</code>; <code>RELEASE_VERSIONING.md</code>; <code>PRIVACY.md</code>; <code>sci_cli/commands.py</code>; <code>website/docs/user-guide/features/science-profiles.md</code>; <code>website/docs/user-guide/sessions.md</code>; <code>plugins/boltzmann/README.md</code>; and the installed canonical Boltzmann catalog and enzyme-tools guide. This was a documentation task; no scientific jobs, model requests, authentication changes or deployment tests were run.
                  </p>
                  <p className="as-text">
                    Documentation checks reflect the inspected source on 23 October 2026. They do not independently establish every historical test outcome or service claim. The website copy is proposed editorial content, not an announcement that a new release has been published.
                  </p>
                </div> */}
              </div>

              <div className="as-nav-footer">
                <button
                  onClick={() => scrollToSection("part-b", 1)}
                  className="as-footer-action-btn secondary"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous: Part B: Product Guide</span>
                </button>

                <button
                  onClick={() => scrollToSection("part-a", 0)}
                  className="as-footer-action-btn secondary"
                >
                  <ArrowLeft className="w-4 h-4" style={{ transform: "rotate(90deg)" }} />
                  <span>Back to Top</span>
                </button>

                <a
                  href={GITHUB_REPO_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="as-footer-action-btn primary"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Agent Scientist Repository</span>
                </a>
              </div>
            </section>
            )}

          </div>
        </main>
      </div>
    </div>
  );
};

export default AgentScientist;
